import React from 'react';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { batchIsSettled } from './useBatchRun';

// One sentence for the confirm step: how many of the selection will actually
// run and why the rest won't. Shared phrasing between the bulk bar and the
// admin pane so the two surfaces never drift.
export function previewLine(preview, { noun = 'selected' } = {}) {
  if (!preview) return '';
  const skips = [];
  if (preview.notTargeted > 0) {
    skips.push(`${preview.notTargeted} ${preview.notTargeted === 1 ? "doesn't" : "don't"} match targeting`);
  }
  if (preview.fresh > 0) skips.push(`${preview.fresh} cached/skipped`);
  if (preview.pending > 0) skips.push(`${preview.pending} already processing`);
  const base = `Runs on ${preview.willRun} of ${preview.selected} ${noun}`;
  return skips.length ? `${base} — ${skips.join('; ')}.` : `${base}.`;
}

// Live progress + per-contract results for a polled batch run.
export default function BatchRunProgress({ batch }) {
  if (!batch) return null;
  const c = batch.counts;
  const settledCount = c.done + c.failed + c.skippedFresh + (c.skippedPending || 0);
  const finished = batchIsSettled(batch);
  const failedItems = batch.items.filter((i) => i.status === 'failed');

  const parts = [`${c.done} done`];
  if (c.failed > 0) parts.push(`${c.failed} failed`);
  if (c.skippedFresh > 0) parts.push(`${c.skippedFresh} skipped (cached)`);
  if (c.skippedPending > 0) parts.push(`${c.skippedPending} already processing`);

  return (
    <div className="stack-sm" aria-live="polite">
      <div className="row" style={{ gap: 8, alignItems: 'center' }}>
        {finished
          ? (failedItems.length > 0
            ? <AlertCircle className="w-4 h-4" style={{ color: 'var(--text-critical)', flexShrink: 0 }} />
            : <CheckCircle2 className="w-4 h-4" style={{ color: 'var(--text-green)', flexShrink: 0 }} />)
          : <Loader2 className="w-4 h-4 animate-spin" style={{ flexShrink: 0 }} />}
        <span style={{ fontSize: 13 }}>
          {finished
            ? `Batch finished: ${parts.join(', ')}.`
            : `Running… ${settledCount} of ${c.items} processed (${parts.join(', ')}).`}
        </span>
      </div>
      {c.notTargeted > 0 && (
        <div className="muted" style={{ fontSize: 12 }}>
          {c.notTargeted} of the {c.selected} selected {c.notTargeted === 1 ? "doesn't" : "don't"} match this schema's targeting and {c.notTargeted === 1 ? 'was' : 'were'} left out.
        </div>
      )}
      {finished && failedItems.length > 0 && (
        <div className="callout red">
          <div className="callout-icon"><AlertCircle className="w-4 h-4" /></div>
          <div className="callout-body" style={{ fontSize: 12.5, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {failedItems.map((i) => (
              <div key={i.contractId}>
                <strong>{i.contractTitle}</strong>
                {i.error ? ` — ${i.error}` : ''}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
